import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import './JourneyPlanner.css';
import CampusContributionPanel, { getCampusVoterId } from './CampusContributionPanel';

const CAMPUS_BOUNDS = {
  south: 12.978,
  west: 80.217,
  north: 13.0115,
  east: 80.2485,
};

const QUICK_DESTINATIONS = [
  { label: 'NAC2', query: 'NAC2' },
  { label: 'CLT', query: 'CLT' },
  { label: 'Main Gate', query: 'Main Gate' },
  { label: 'Research Park', query: 'Research Park' },
];

function SearchIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="m21 21-4.35-4.35m2.35-5.15a7.5 7.5 0 1 1-15 0 7.5 7.5 0 0 1 15 0Z" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    </svg>
  );
}

function ArrowIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 12h14m-5-5 5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LocationIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 21s6-5.1 6-11a6 6 0 1 0-12 0c0 5.9 6 11 6 11Z" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="12" cy="10" r="2.2" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function SparkIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 3c.65 3.25 2.75 5.35 6 6-3.25.65-5.35 2.75-6 6-.65-3.25-2.75-5.35-6-6 3.25-.65 5.35-2.75 6-6Z" fill="currentColor" />
      <path d="M19 15c.28 1.38 1.12 2.22 2.5 2.5-1.38.28-2.22 1.12-2.5 2.5-.28-1.38-1.12-2.22-2.5-2.5 1.38-.28 2.22-1.12 2.5-2.5Z" fill="currentColor" opacity=".72" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function TransitIcon({ mode, size = 20 }) {
  if (mode === 'WALK') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="13" cy="4.5" r="2" fill="currentColor" />
        <path d="m10.5 8 3 2.5 2.5 4m-5.5-6.5-2 4 3 2.5-2 5m4-9.5-1 5 4 4.5m-5-7.5H7.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (mode === 'WAIT') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 7.5V12l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (mode === 'ALIGHT') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 3v12m0 0-4-4m4 4 4-4M5 20h14" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="5" y="3.5" width="14" height="15" rx="3" stroke="currentColor" strokeWidth="1.8" />
      <path d="M7.5 13.5h9M8 7h8M8 20v-1.5m8 1.5v-1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="8.5" cy="16" r="1" fill="currentColor" />
      <circle cx="15.5" cy="16" r="1" fill="currentColor" />
    </svg>
  );
}

function isInsideCampus(location) {
  return Boolean(
    location &&
      Number(location.lat) >= CAMPUS_BOUNDS.south &&
      Number(location.lat) <= CAMPUS_BOUNDS.north &&
      Number(location.lng) >= CAMPUS_BOUNDS.west &&
      Number(location.lng) <= CAMPUS_BOUNDS.east
  );
}

function formatArrival(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDistance(metres) {
  const value = Number(metres);
  if (!Number.isFinite(value)) return '';
  if (value >= 1000) return `${(value / 1000).toFixed(value >= 10000 ? 0 : 1)} km`;
  return `${Math.round(value)} m`;
}

function destinationPayload(item) {
  if (!item) return null;
  return {
    id: item.id,
    entityId: item.entityId,
    name: item.name,
    fullName: item.fullName,
    type: item.type,
    lat: item.lat,
    lng: item.lng,
    source: item.source,
    building: item.building,
  };
}

function verificationLabel(item) {
  const status = item?.verification?.status;
  if (status === 'official_source') return 'Official source';
  if (status === 'curated_official_map') return 'Curated from IITM map';
  if (status === 'openstreetmap') return 'OpenStreetMap';
  if (status === 'admin_verified') return 'Admin verified';
  if (status === 'community_verified') return `${item.verification.confirmed || 0} confirmations`;
  return '';
}

function SearchResult({ item, onSelect }) {
  const meta = [item.subtitle, item.distanceM != null ? `${formatDistance(item.distanceM)} away` : '']
    .filter(Boolean)
    .join(' · ');
  const buildingLine = item.building
    ? [item.building.shortName || item.building.name, item.building.roomNo ? `Room ${item.building.roomNo}` : '']
        .filter(Boolean)
        .join(' · ')
    : '';
  const verified = verificationLabel(item);

  return (
    <button
      type="button"
      className={`journey-search-result ${item.routeable ? '' : 'is-unmapped'}`}
      onClick={() => item.routeable && onSelect(item)}
      disabled={!item.routeable}
    >
      <span className="journey-search-result-icon"><LocationIcon size={18} /></span>
      <span className="journey-search-result-copy">
        <strong>{item.name}</strong>
        <span>{buildingLine || meta}</span>
        {buildingLine && <small>{meta}</small>}
        {!item.routeable && <small>Building location has not been mapped yet</small>}
        {verified && <small className="journey-verification-chip">✓ {verified}</small>}
      </span>
      {item.routeable && <span className="journey-search-result-arrow"><ArrowIcon size={16} /></span>}
    </button>
  );
}

function CommunityCandidate({ item, backend, onVerified }) {
  const [current, setCurrent] = useState(item);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function vote(value) {
    setBusy(true);
    setError('');
    try {
      const id = String(current.entityId || current.id).replace(/^community:/, '');
      const response = await axios.post(
        `${backend}/api/campus/contributions/${id}/vote`,
        { vote: value, voterId: getCampusVoterId() },
        { timeout: 8000 }
      );
      const next = response.data?.item || current;
      setCurrent(next);
      if (next.routeable) onVerified?.(next);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Vote could not be recorded.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="journey-community-candidate">
      <div className="journey-community-head">
        <span className="journey-community-pin"><LocationIcon size={17} /></span>
        <div>
          <strong>{current.name}</strong>
          <small>Community-submitted location · not trusted for routing yet</small>
        </div>
      </div>
      {(current.verification?.evidenceUrl || current.verification?.evidenceNote) && (
        <div className="journey-community-evidence">
          {current.verification?.evidenceNote && <span>“{current.verification.evidenceNote}”</span>}
          {current.verification?.evidenceUrl && (
            <a href={current.verification.evidenceUrl} target="_blank" rel="noreferrer">Review ground-truth source ↗</a>
          )}
        </div>
      )}
      <div className="journey-community-votes">
        <span><b>{current.verification?.confirmed || 0}</b> confirm</span>
        <span><b>{current.verification?.rejected || 0}</b> reject</span>
        <button type="button" disabled={busy} onClick={() => vote('confirm')}>It is correct</button>
        <button type="button" disabled={busy} onClick={() => vote('reject')}>Wrong pin</button>
      </div>
      {error && <small className="journey-community-error">{error}</small>}
    </div>
  );
}

function SearchField({
  backend,
  label,
  placeholder,
  value,
  selected,
  onValueChange,
  onSelect,
  onSuggest,
  origin,
  autoFocus,
}) {
  const [results, setResults] = useState([]);
  const [communityCandidates, setCommunityCandidates] = useState([]);
  const [canSuggest, setCanSuggest] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const requestRef = useRef(0);

  useEffect(() => {
    if (selected || !value.trim() || !backend) {
      setResults([]);
      setCommunityCandidates([]);
      setCanSuggest(false);
      setMessage('');
      return undefined;
    }

    const requestId = ++requestRef.current;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setMessage('');
      try {
        const response = await axios.get(`${backend}/api/campus/search`, {
          params: {
            q: value.trim(),
            limit: 12,
            ...(origin ? { lat: origin.lat, lng: origin.lng } : {}),
          },
          timeout: 8000,
        });
        if (requestRef.current !== requestId) return;
        const nextResults = response.data?.results || [];
        setResults(nextResults);
        setCommunityCandidates(response.data?.communityCandidates || []);
        setCanSuggest(Boolean(response.data?.canSuggest));
        if (!nextResults.length && !(response.data?.communityCandidates || []).length) {
          setMessage('No trusted match yet. Add the location and let the IITM community verify it.');
        }
      } catch (error) {
        if (requestRef.current !== requestId) return;
        setResults([]);
        setCommunityCandidates([]);
        setCanSuggest(true);
        setMessage(error?.response?.data?.message || 'Search is temporarily unavailable.');
      } finally {
        if (requestRef.current === requestId) setLoading(false);
      }
    }, 260);

    return () => window.clearTimeout(timer);
  }, [backend, origin?.lat, origin?.lng, selected, value]);

  const hasDropdown = results.length > 0 || communityCandidates.length > 0 || message || (canSuggest && value.trim());

  return (
    <div className="journey-field-group">
      <label>{label}</label>
      <div className={`journey-search-box ${selected ? 'has-selection' : ''}`}>
        <SearchIcon size={19} />
        <input
          value={selected ? selected.name : value}
          onChange={(event) => {
            if (selected) onSelect(null);
            onValueChange(event.target.value);
          }}
          placeholder={placeholder}
          autoFocus={autoFocus}
          aria-label={label}
        />
        {loading && <span className="journey-mini-loader" aria-label="Searching" />}
        {(selected || value) && !loading && (
          <button
            type="button"
            className="journey-field-clear"
            onClick={() => {
              onSelect(null);
              onValueChange('');
            }}
            aria-label={`Clear ${label.toLowerCase()}`}
          >
            <CloseIcon />
          </button>
        )}
      </div>

      {!selected && hasDropdown && (
        <div className="journey-search-results" role="listbox">
          {results.map((item) => (
            <SearchResult key={item.id} item={item} onSelect={onSelect} />
          ))}
          {communityCandidates.length > 0 && (
            <div className="journey-community-section">
              <div className="journey-community-title">Help verify nearby suggestions</div>
              {communityCandidates.map((item) => (
                <CommunityCandidate key={item.id} item={item} backend={backend} onVerified={onSelect} />
              ))}
            </div>
          )}
          {message && <div className="journey-search-message">{message}</div>}
          {canSuggest && value.trim() && onSuggest && (
            <button type="button" className="journey-add-location" onClick={() => onSuggest(value.trim())}>
              <span className="journey-add-location-plus">＋</span>
              <span><strong>Add “{value.trim()}”</strong><small>Pin it, add evidence and start community verification</small></span>
              <ArrowIcon size={17} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function transportCopy(option) {
  const isCart = option?.mode === 'CART';
  return {
    label: isCart ? 'Campus cart' : 'Campus bus',
    shortLabel: isCart ? 'Cart' : 'Bus',
  };
}

function publicStepCopy(step, option) {
  const transport = transportCopy(option);

  if (step?.mode === 'WAIT') {
    const waitMinutes = Number(step.durationMinutes || step.live?.waitMinutes || 0);
    return {
      title: waitMinutes <= 0 ? `${transport.label} is arriving` : `Wait about ${waitMinutes} min`,
      detail: step.live?.status === 'SCHEDULE_ESTIMATE'
        ? `Using the usual ${transport.shortLabel.toLowerCase()} service frequency.`
        : `${transport.label} towards ${option?.destinationDirection || 'your destination'}`,
    };
  }

  if (step?.mode === 'BUS' || step?.mode === 'CART') {
    return {
      title: `Take the ${transport.label.toLowerCase()}`,
      detail: step.detail,
    };
  }

  return { title: step?.title, detail: step?.detail };
}

function StepTimeline({ option }) {
  return (
    <div className="journey-timeline">
      {(option?.steps || []).map((step, index) => {
        const copy = publicStepCopy(step, option);
        return (
        <div className="journey-step" key={`${step.mode}-${index}-${copy.title}`}>
          <div className={`journey-step-icon mode-${String(step.mode || '').toLowerCase()}`}>
            <TransitIcon mode={step.mode} size={19} />
          </div>
          <div className="journey-step-line" aria-hidden="true" />
          <div className="journey-step-copy">
            <strong>{copy.title}</strong>
            <span>{copy.detail}</span>
            {step.live?.status === 'LIVE' && (
              <small className="journey-live-chip">Live vehicle · {step.live.confidence} confidence</small>
            )}
            {step.live?.status === 'SCHEDULE_ESTIMATE' && (
              <small className="journey-estimate-chip">Service-frequency estimate</small>
            )}
          </div>
          {Number(step.durationMinutes) > 0 && <span className="journey-step-time">{step.durationMinutes} min</span>}
        </div>
        );
      })}
    </div>
  );
}

export default function JourneyPlanner({
  backend,
  userLocation,
  requestUserLocation,
  onPlanChange,
  onLocationUpdate,
  onFocus,
  campusMapPicking = false,
  pickedCampusPoint,
  onBeginCampusMapPick,
  onEndCampusMapPick,
  onClearCampusPickedPoint,
}) {
  const [open, setOpen] = useState(false);
  const [originMode, setOriginMode] = useState('current');
  const [originQuery, setOriginQuery] = useState('');
  const [manualOrigin, setManualOrigin] = useState(null);
  const [destinationQuery, setDestinationQuery] = useState('');
  const [destination, setDestination] = useState(null);
  const preference = 'transit';
  const [planResponse, setPlanResponse] = useState(null);
  const [activeOption, setActiveOption] = useState(null);
  const [planning, setPlanning] = useState(false);
  const [error, setError] = useState('');
  const [navigating, setNavigating] = useState(false);
  const [liveOrigin, setLiveOrigin] = useState(null);
  const [contributionOpen, setContributionOpen] = useState(false);
  const [contributionName, setContributionName] = useState('');
  const watchIdRef = useRef(null);
  const navigatingRef = useRef(false);
  const hasCollapsedForNavigationRef = useRef(false);
  const requestControllerRef = useRef(null);
  const activeRequestKeyRef = useRef('');
  const lastSuccessfulRequestKeyRef = useRef('');
  const buildJourneyRef = useRef(null);
  const onPlanChangeRef = useRef(onPlanChange);
  const onFocusRef = useRef(onFocus);
  const effectiveOrigin = originMode === 'manual' ? manualOrigin : (liveOrigin || userLocation);
  const originLat = Number(effectiveOrigin?.lat);
  const originLng = Number(effectiveOrigin?.lng);
  const destinationKey = destination
    ? String(destination.entityId || destination.id || `${destination.lat}:${destination.lng}`)
    : '';

  useEffect(() => {
    onPlanChangeRef.current = onPlanChange;
  }, [onPlanChange]);

  useEffect(() => {
    onFocusRef.current = onFocus;
  }, [onFocus]);

  useEffect(() => {
    navigatingRef.current = navigating;
  }, [navigating]);

  const publishOption = useCallback((response, option) => {
    if (!response || !option) {
      onPlanChangeRef.current?.(null);
      return;
    }
    onPlanChangeRef.current?.({ ...response, recommended: option, navigationActive: navigatingRef.current });
  }, []);

  const buildJourney = useCallback(async ({ quiet = false, force = false } = {}) => {
    if (!backend || !destination || !Number.isFinite(originLat) || !Number.isFinite(originLng)) return;

    const origin = {
      lat: originLat,
      lng: originLng,
      name: effectiveOrigin?.name || (originMode === 'current' ? 'Your location' : 'Selected start'),
    };

    if (!isInsideCampus(origin)) {
      setError('The selected start is outside the IIT Madras campus navigation area. Choose a campus landmark as the start.');
      return;
    }

    const requestKey = [
      backend,
      destinationKey,
      originLat.toFixed(6),
      originLng.toFixed(6),
      preference,
    ].join('|');

    // Do not send the same automatic request again after a React re-render.
    if (!force && (
      activeRequestKeyRef.current === requestKey ||
      lastSuccessfulRequestKeyRef.current === requestKey
    )) {
      return;
    }

    // Cancel an older in-flight request when the user changes start/destination/mode.
    requestControllerRef.current?.abort();
    const controller = new AbortController();
    requestControllerRef.current = controller;
    activeRequestKeyRef.current = requestKey;

    if (!quiet) setPlanning(true);
    setError('');

    try {
      const response = await axios.post(
        `${backend}/api/campus/journey`,
        {
          origin,
          destination: destinationPayload(destination),
          preference,
        },
        { timeout: 15000, signal: controller.signal }
      );

      const data = response.data;
      lastSuccessfulRequestKeyRef.current = requestKey;
      setPlanResponse(data);
      setActiveOption(data.recommended);
      publishOption(data, data.recommended);

      // JourneyMapLayer owns the route viewport. Do not refocus the map here;
      // doing so fought fitBounds and caused the visible zoom-in/zoom-out loop.
    } catch (requestError) {
      if (axios.isCancel(requestError) || requestError?.code === 'ERR_CANCELED') return;

      const status = requestError?.response?.status;
      if (status === 429) {
        const retryAfter = Number(requestError?.response?.headers?.['retry-after']);
        setError(
          Number.isFinite(retryAfter) && retryAfter > 0
            ? `Journey planning is cooling down. Try again in ${retryAfter} seconds.`
            : 'Too many journey requests were sent. Please wait about a minute and try again.'
        );
      } else {
        setError(requestError?.response?.data?.message || 'Unable to build this journey right now.');
      }

      if (!quiet) {
        setPlanResponse(null);
        setActiveOption(null);
        onPlanChangeRef.current?.(null);
      }
    } finally {
      if (activeRequestKeyRef.current === requestKey) activeRequestKeyRef.current = '';
      if (requestControllerRef.current === controller) requestControllerRef.current = null;
      if (!quiet) setPlanning(false);
    }
  }, [backend, destination, destinationKey, effectiveOrigin?.name, originLat, originLng, originMode, preference, publishOption]);

  useEffect(() => {
    buildJourneyRef.current = buildJourney;
  }, [buildJourney]);

  useEffect(() => {
    if (!destination || !Number.isFinite(originLat) || !Number.isFinite(originLng) || navigating) return undefined;
    const timer = window.setTimeout(() => buildJourney({ quiet: false, force: false }), 450);
    return () => window.clearTimeout(timer);
  }, [buildJourney, destinationKey, navigating, originLat, originLng, preference]);

  useEffect(() => {
    if (!navigating) return undefined;
    const refresh = () => buildJourneyRef.current?.({ quiet: true, force: true });
    const interval = window.setInterval(refresh, 20000);
    return () => window.clearInterval(interval);
  }, [navigating]);

  useEffect(() => () => {
    requestControllerRef.current?.abort();
    if (watchIdRef.current != null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }
  }, []);

  const startLiveNavigation = () => {
    if (navigating) {
      navigatingRef.current = false;
      hasCollapsedForNavigationRef.current = false;
      setNavigating(false);
      setLiveOrigin(null);
      if (watchIdRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      watchIdRef.current = null;
      if (planResponse && activeOption) {
        onPlanChangeRef.current?.({ ...planResponse, recommended: activeOption, navigationActive: false });
      }
      return;
    }

    if (!navigator.geolocation) {
      setError('Live navigation is not supported by this browser.');
      return;
    }

    setOriginMode('current');
    navigatingRef.current = true;
    hasCollapsedForNavigationRef.current = false;
    setNavigating(true);
    setError('');

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          name: 'Your live location',
        };
        setLiveOrigin(location);
        onLocationUpdate?.(location);

        // Switch from the directions sheet to the full map once, as soon as
        // live location is available. The user can reopen directions without
        // the next GPS update closing the panel again.
        if (!hasCollapsedForNavigationRef.current) {
          hasCollapsedForNavigationRef.current = true;
          setOpen(false);
        }
        if (planResponse && activeOption) {
          onPlanChangeRef.current?.({ ...planResponse, recommended: activeOption, navigationActive: true });
        }
      },
      (geoError) => {
        navigatingRef.current = false;
        hasCollapsedForNavigationRef.current = false;
        setNavigating(false);
        if (planResponse && activeOption) {
          onPlanChangeRef.current?.({ ...planResponse, recommended: activeOption, navigationActive: false });
        }
        setError(geoError?.code === 1 ? 'Location permission is blocked. Allow location in the browser site settings to start live navigation.' : geoError?.message || 'Unable to follow your live location.');
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 12000 }
    );
  };

  const clearJourney = () => {
    requestControllerRef.current?.abort();
    activeRequestKeyRef.current = '';
    lastSuccessfulRequestKeyRef.current = '';
    setDestination(null);
    setDestinationQuery('');
    setPlanResponse(null);
    setActiveOption(null);
    setError('');
    onPlanChange?.(null);
    if (navigating) startLiveNavigation();
  };

  async function chooseQuickDestination(query) {
    if (!backend || !query) return;

    setPlanning(true);
    setError('');

    try {
      const params = {
        q: query,
        limit: 12,
      };

      if (Number.isFinite(originLat) && Number.isFinite(originLng)) {
        params.lat = originLat;
        params.lng = originLng;
      }

      const response = await axios.get(`${backend}/api/campus/search`, {
        params,
        timeout: 8000,
      });

      const match = (response.data?.results || []).find(
        (item) => item?.routeable
      );

      if (!match) {
        setError(`No trusted mapped location was found for “${query}”.`);
        return;
      }

      requestControllerRef.current?.abort();
      activeRequestKeyRef.current = '';
      lastSuccessfulRequestKeyRef.current = '';

      setPlanResponse(null);
      setActiveOption(null);
      setDestinationQuery('');
      setDestination(match);

      onFocusRef.current?.(match);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          `Unable to find “${query}” right now.`
      );
    } finally {
      setPlanning(false);
    }
  }

  const originReady = Boolean(effectiveOrigin && isInsideCampus(effectiveOrigin));

  return (
    <>
      {!open && (
        <button
          type="button"
          className={`journey-launcher ${navigating ? 'is-navigation-active' : ''}`}
          onClick={() => setOpen(true)}
        >
          <span className="journey-launcher-orb"><SparkIcon size={18} /></span>
          <span>
            <strong>{navigating ? '(Beta) Live navigation active' : '(Beta) Where are you going?'}</strong>
            <small>{navigating ? '(Beta) Tap to view directions' : '(Beta) Search buildings, labs, offices & people'}</small>
          </span>
          <ArrowIcon size={18} />
        </button>
      )}

      {open && !campusMapPicking && <button type="button" className="journey-backdrop" onClick={() => setOpen(false)} aria-label="Close journey planner" />}

      <aside className={`journey-panel ${open ? 'is-open' : ''} ${campusMapPicking ? 'is-map-picking' : ''}`} aria-hidden={!open}>
        <div className="journey-panel-handle" aria-hidden="true" />
        <header className="journey-panel-header">
          <div>
            <span className="journey-eyebrow"><SparkIcon size={15} /> IITM CAMPUS NAVIGATOR</span>
            <h2>Take Me There</h2>
            <p>Door-to-building guidance with live campus buses and carts.</p>
          </div>
          <button type="button" className="journey-close" onClick={() => setOpen(false)} aria-label="Close">
            <CloseIcon />
          </button>
        </header>

        <div className="journey-panel-scroll">
          {!activeOption && (
            <section className="journey-search-card">
              <div className="journey-origin-toggle" role="tablist" aria-label="Choose start location">
                <button
                  type="button"
                  className={originMode === 'current' ? 'is-active' : ''}
                  onClick={() => setOriginMode('current')}
                >
                  <LocationIcon size={17} /> Current location
                </button>
                <button
                  type="button"
                  className={originMode === 'manual' ? 'is-active' : ''}
                  onClick={() => setOriginMode('manual')}
                >
                  Choose a start
                </button>
              </div>

              {originMode === 'current' ? (
                <div className={`journey-current-location ${originReady ? 'is-ready' : ''}`}>
                  <span className="journey-location-pulse" />
                  <div>
                    <strong>{originReady ? 'Current campus location ready' : 'Current location needed'}</strong>
                    <span>
                      {originReady
                        ? `${Number(effectiveOrigin.lat).toFixed(5)}, ${Number(effectiveOrigin.lng).toFixed(5)}`
                        : 'Allow location, or choose a campus building as your start.'}
                    </span>
                  </div>
                  {!originReady && (
                    <button type="button" onClick={requestUserLocation}>Use location</button>
                  )}
                </div>
              ) : (
                <SearchField
                  backend={backend}
                  label="Start from"
                  placeholder="Search a gate, hostel, building or stop"
                  value={originQuery}
                  selected={manualOrigin}
                  onValueChange={setOriginQuery}
                  onSelect={(item) => {
                    setManualOrigin(item);
                    if (item) setOriginQuery('');
                  }}
                  origin={userLocation}
                />
              )}

              <SearchField
                backend={backend}
                label="Destination"
                placeholder="Try NAC2, a lab, office or professor"
                value={destinationQuery}
                selected={destination}
                onValueChange={setDestinationQuery}
                onSelect={(item) => {
                  requestControllerRef.current?.abort();
                  activeRequestKeyRef.current = '';
                  lastSuccessfulRequestKeyRef.current = '';
                  setDestination(item);
                  if (item) {
                    setDestinationQuery('');
                    onFocusRef.current?.(item);
                  }
                }}
                origin={effectiveOrigin}
                autoFocus={originReady}
                onSuggest={(name) => {
                  setContributionName(name);
                  onClearCampusPickedPoint?.();
                  setContributionOpen(true);
                }}
              />

              {!destination && (
                <div className="journey-quick-row">
                  <span>Popular</span>
                  {QUICK_DESTINATIONS.map((item) => (
                    <button type="button" key={item.query} onClick={() => chooseQuickDestination(item.query)}>
                      {item.label}
                    </button>
                  ))}
                </div>
              )}

              <div className="journey-network-priority">
                <TransitIcon mode="BUS" size={17} />
                <span>Automatically choosing one route with the most campus bus/cart coverage.</span>
              </div>

              {planning && (
                <div className="journey-planning-state">
                  <span className="journey-route-loader" />
                  <div>
                    <strong>Finding your best campus route</strong>
                    <span>Comparing walking, live vehicles and campus stops…</span>
                  </div>
                </div>
              )}
              {error && <div className="journey-error">{error}</div>}
            </section>
          )}

          {activeOption && planResponse && (
            <>
              <section className="journey-result-hero">
                <div className="journey-result-topline">
                  <span className="journey-result-mode">
                    <TransitIcon mode={activeOption.mode} size={18} />
                    {activeOption.type === 'walking' ? 'Walk' : activeOption.mode === 'CART' ? 'Cart + walk' : 'Bus + walk'}
                  </span>
                  <span className={`journey-confidence confidence-${activeOption.confidence || 'medium'}`}>
                    {activeOption.confidence || 'medium'} confidence
                  </span>
                </div>
                <div className="journey-arrival-grid">
                  <div>
                    <small>ARRIVE</small>
                    <strong>{formatArrival(activeOption.arrivalTime)}</strong>
                  </div>
                  <div>
                    <small>TOTAL</small>
                    <strong>{activeOption.durationMinutes} min</strong>
                  </div>
                  <div>
                    <small>DISTANCE</small>
                    <strong>{formatDistance(activeOption.distanceM)}</strong>
                  </div>
                </div>
                <div className="journey-destination-line">
                  <span className="journey-destination-dot" />
                  <div>
                    <small>DESTINATION</small>
                    <strong>{planResponse.destination?.name}</strong>
                    {destination?.building?.roomNo && (
                      <span>{destination.building.shortName || destination.building.name} · Room {destination.building.roomNo}</span>
                    )}
                  </div>
                </div>
              </section>

              {activeOption.liveVehicle && (
                <section className="journey-live-card">
                  <span className="journey-live-dot" />
                  <div>
                    <small>LIVE CAMPUS {activeOption.mode === 'CART' ? 'CART' : 'BUS'}</small>
                    <strong>{activeOption.mode === 'CART' ? 'Campus cart' : 'Campus bus'}</strong>
                    <span>Reaches your stop in about {activeOption.liveVehicle.etaMinutes} min</span>
                  </div>
                  <span className="journey-live-badge">LIVE</span>
                </section>
              )}

              <section className="journey-directions-card">
                <div className="journey-section-title">
                  <div>
                    <span>YOUR ROUTE</span>
                    <h3>{activeOption.title}</h3>
                  </div>
                  <button type="button" onClick={clearJourney}>Change</button>
                </div>
                <StepTimeline option={activeOption} />
              </section>



              {error && <div className="journey-error">{error}</div>}

            </>
          )}
        </div>

        {activeOption && planResponse && (
          <div className="journey-action-bar">
            <button type="button" className={navigating ? 'is-navigating' : ''} onClick={startLiveNavigation}>
              <span>{navigating ? 'Stop live navigation' : 'Start live navigation'}</span>
              <small>{navigating ? 'Return to a static route view' : 'Hide this panel and follow your position on the map'}</small>
              <ArrowIcon size={19} />
            </button>
          </div>
        )}
      </aside>

      <CampusContributionPanel
        open={contributionOpen}
        mapPicking={campusMapPicking}
        backend={backend}
        initialName={contributionName}
        userLocation={userLocation}
        pickedPoint={pickedCampusPoint}
        onBeginMapPick={onBeginCampusMapPick}
        onEndMapPick={onEndCampusMapPick}
        onSubmitted={() => {
          setDestinationQuery(contributionName);
        }}
        onClose={() => {
          setContributionOpen(false);
          onEndCampusMapPick?.();
          onClearCampusPickedPoint?.();
        }}
      />
    </>
  );
}

// import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// import axios from 'axios';
// import './JourneyPlanner.css';
// import CampusContributionPanel, { getCampusVoterId } from './CampusContributionPanel';

// const CAMPUS_BOUNDS = {
//   south: 12.978,
//   west: 80.217,
//   north: 13.0115,
//   east: 80.2485,
// };

// const QUICK_DESTINATIONS = [
//   { label: 'NAC2', query: 'NAC2' },
//   { label: 'CLT', query: 'CLT' },
//   { label: 'Main Gate', query: 'Main Gate' },
//   { label: 'Research Park', query: 'Research Park' },
// ];

// function SearchIcon({ size = 20 }) {
//   return (
//     <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
//       <path d="m21 21-4.35-4.35m2.35-5.15a7.5 7.5 0 1 1-15 0 7.5 7.5 0 0 1 15 0Z" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
//     </svg>
//   );
// }

// function ArrowIcon({ size = 18 }) {
//   return (
//     <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
//       <path d="M5 12h14m-5-5 5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//     </svg>
//   );
// }

// function LocationIcon({ size = 20 }) {
//   return (
//     <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
//       <path d="M12 21s6-5.1 6-11a6 6 0 1 0-12 0c0 5.9 6 11 6 11Z" stroke="currentColor" strokeWidth="1.8" />
//       <circle cx="12" cy="10" r="2.2" stroke="currentColor" strokeWidth="1.8" />
//     </svg>
//   );
// }

// function SparkIcon({ size = 18 }) {
//   return (
//     <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
//       <path d="M12 3c.65 3.25 2.75 5.35 6 6-3.25.65-5.35 2.75-6 6-.65-3.25-2.75-5.35-6-6 3.25-.65 5.35-2.75 6-6Z" fill="currentColor" />
//       <path d="M19 15c.28 1.38 1.12 2.22 2.5 2.5-1.38.28-2.22 1.12-2.5 2.5-.28-1.38-1.12-2.22-2.5-2.5 1.38-.28 2.22-1.12 2.5-2.5Z" fill="currentColor" opacity=".72" />
//     </svg>
//   );
// }

// function CloseIcon() {
//   return (
//     <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
//       <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
//     </svg>
//   );
// }

// function ChevronIcon({ open }) {
//   return (
//     <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ transform: open ? 'rotate(180deg)' : 'none' }}>
//       <path d="m7 9 5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//     </svg>
//   );
// }

// function TransitIcon({ mode, size = 20 }) {
//   if (mode === 'WALK') {
//     return (
//       <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
//         <circle cx="13" cy="4.5" r="2" fill="currentColor" />
//         <path d="m10.5 8 3 2.5 2.5 4m-5.5-6.5-2 4 3 2.5-2 5m4-9.5-1 5 4 4.5m-5-7.5H7.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
//       </svg>
//     );
//   }
//   if (mode === 'WAIT') {
//     return (
//       <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
//         <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.8" />
//         <path d="M12 7.5V12l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//       </svg>
//     );
//   }
//   if (mode === 'ALIGHT') {
//     return (
//       <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
//         <path d="M12 3v12m0 0-4-4m4 4 4-4M5 20h14" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
//       </svg>
//     );
//   }
//   return (
//     <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
//       <rect x="5" y="3.5" width="14" height="15" rx="3" stroke="currentColor" strokeWidth="1.8" />
//       <path d="M7.5 13.5h9M8 7h8M8 20v-1.5m8 1.5v-1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//       <circle cx="8.5" cy="16" r="1" fill="currentColor" />
//       <circle cx="15.5" cy="16" r="1" fill="currentColor" />
//     </svg>
//   );
// }

// function isInsideCampus(location) {
//   return Boolean(
//     location &&
//       Number(location.lat) >= CAMPUS_BOUNDS.south &&
//       Number(location.lat) <= CAMPUS_BOUNDS.north &&
//       Number(location.lng) >= CAMPUS_BOUNDS.west &&
//       Number(location.lng) <= CAMPUS_BOUNDS.east
//   );
// }

// function formatArrival(value) {
//   if (!value) return '—';
//   const date = new Date(value);
//   if (Number.isNaN(date.getTime())) return '—';
//   return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
// }

// function formatDistance(metres) {
//   const value = Number(metres);
//   if (!Number.isFinite(value)) return '';
//   if (value >= 1000) return `${(value / 1000).toFixed(value >= 10000 ? 0 : 1)} km`;
//   return `${Math.round(value)} m`;
// }

// function destinationPayload(item) {
//   if (!item) return null;
//   return {
//     id: item.id,
//     entityId: item.entityId,
//     name: item.name,
//     fullName: item.fullName,
//     type: item.type,
//     lat: item.lat,
//     lng: item.lng,
//     source: item.source,
//     building: item.building,
//   };
// }

// function verificationLabel(item) {
//   const status = item?.verification?.status;
//   if (status === 'official_source') return 'Official source';
//   if (status === 'curated_official_map') return 'Curated from IITM map';
//   if (status === 'openstreetmap') return 'OpenStreetMap';
//   if (status === 'admin_verified') return 'Admin verified';
//   if (status === 'community_verified') return `${item.verification.confirmed || 0} confirmations`;
//   return '';
// }

// function SearchResult({ item, onSelect }) {
//   const meta = [item.subtitle, item.distanceM != null ? `${formatDistance(item.distanceM)} away` : '']
//     .filter(Boolean)
//     .join(' · ');
//   const buildingLine = item.building
//     ? [item.building.shortName || item.building.name, item.building.roomNo ? `Room ${item.building.roomNo}` : '']
//         .filter(Boolean)
//         .join(' · ')
//     : '';
//   const verified = verificationLabel(item);

//   return (
//     <button
//       type="button"
//       className={`journey-search-result ${item.routeable ? '' : 'is-unmapped'}`}
//       onClick={() => item.routeable && onSelect(item)}
//       disabled={!item.routeable}
//     >
//       <span className="journey-search-result-icon"><LocationIcon size={18} /></span>
//       <span className="journey-search-result-copy">
//         <strong>{item.name}</strong>
//         <span>{buildingLine || meta}</span>
//         {buildingLine && <small>{meta}</small>}
//         {!item.routeable && <small>Building location has not been mapped yet</small>}
//         {verified && <small className="journey-verification-chip">✓ {verified}</small>}
//       </span>
//       {item.routeable && <span className="journey-search-result-arrow"><ArrowIcon size={16} /></span>}
//     </button>
//   );
// }

// function CommunityCandidate({ item, backend, onVerified }) {
//   const [current, setCurrent] = useState(item);
//   const [busy, setBusy] = useState(false);
//   const [error, setError] = useState('');

//   async function vote(value) {
//     setBusy(true);
//     setError('');
//     try {
//       const id = String(current.entityId || current.id).replace(/^community:/, '');
//       const response = await axios.post(
//         `${backend}/api/campus/contributions/${id}/vote`,
//         { vote: value, voterId: getCampusVoterId() },
//         { timeout: 8000 }
//       );
//       const next = response.data?.item || current;
//       setCurrent(next);
//       if (next.routeable) onVerified?.(next);
//     } catch (requestError) {
//       setError(requestError?.response?.data?.message || 'Vote could not be recorded.');
//     } finally {
//       setBusy(false);
//     }
//   }

//   return (
//     <div className="journey-community-candidate">
//       <div className="journey-community-head">
//         <span className="journey-community-pin"><LocationIcon size={17} /></span>
//         <div>
//           <strong>{current.name}</strong>
//           <small>Community-submitted location · not trusted for routing yet</small>
//         </div>
//       </div>
//       {(current.verification?.evidenceUrl || current.verification?.evidenceNote) && (
//         <div className="journey-community-evidence">
//           {current.verification?.evidenceNote && <span>“{current.verification.evidenceNote}”</span>}
//           {current.verification?.evidenceUrl && (
//             <a href={current.verification.evidenceUrl} target="_blank" rel="noreferrer">Review ground-truth source ↗</a>
//           )}
//         </div>
//       )}
//       <div className="journey-community-votes">
//         <span><b>{current.verification?.confirmed || 0}</b> confirm</span>
//         <span><b>{current.verification?.rejected || 0}</b> reject</span>
//         <button type="button" disabled={busy} onClick={() => vote('confirm')}>It is correct</button>
//         <button type="button" disabled={busy} onClick={() => vote('reject')}>Wrong pin</button>
//       </div>
//       {error && <small className="journey-community-error">{error}</small>}
//     </div>
//   );
// }

// function SearchField({
//   backend,
//   label,
//   placeholder,
//   value,
//   selected,
//   onValueChange,
//   onSelect,
//   onSuggest,
//   origin,
//   autoFocus,
// }) {
//   const [results, setResults] = useState([]);
//   const [communityCandidates, setCommunityCandidates] = useState([]);
//   const [canSuggest, setCanSuggest] = useState(false);
//   const [loading, setLoading] = useState(false);
//   const [message, setMessage] = useState('');
//   const requestRef = useRef(0);

//   useEffect(() => {
//     if (selected || !value.trim() || !backend) {
//       setResults([]);
//       setCommunityCandidates([]);
//       setCanSuggest(false);
//       setMessage('');
//       return undefined;
//     }

//     const requestId = ++requestRef.current;
//     const timer = window.setTimeout(async () => {
//       setLoading(true);
//       setMessage('');
//       try {
//         const response = await axios.get(`${backend}/api/campus/search`, {
//           params: {
//             q: value.trim(),
//             limit: 12,
//             ...(origin ? { lat: origin.lat, lng: origin.lng } : {}),
//           },
//           timeout: 8000,
//         });
//         if (requestRef.current !== requestId) return;
//         const nextResults = response.data?.results || [];
//         setResults(nextResults);
//         setCommunityCandidates(response.data?.communityCandidates || []);
//         setCanSuggest(Boolean(response.data?.canSuggest));
//         if (!nextResults.length && !(response.data?.communityCandidates || []).length) {
//           setMessage('No trusted match yet. Add the location and let the IITM community verify it.');
//         }
//       } catch (error) {
//         if (requestRef.current !== requestId) return;
//         setResults([]);
//         setCommunityCandidates([]);
//         setCanSuggest(true);
//         setMessage(error?.response?.data?.message || 'Search is temporarily unavailable.');
//       } finally {
//         if (requestRef.current === requestId) setLoading(false);
//       }
//     }, 260);

//     return () => window.clearTimeout(timer);
//   }, [backend, origin?.lat, origin?.lng, selected, value]);

//   const hasDropdown = results.length > 0 || communityCandidates.length > 0 || message || (canSuggest && value.trim());

//   return (
//     <div className="journey-field-group">
//       <label>{label}</label>
//       <div className={`journey-search-box ${selected ? 'has-selection' : ''}`}>
//         <SearchIcon size={19} />
//         <input
//           value={selected ? selected.name : value}
//           onChange={(event) => {
//             if (selected) onSelect(null);
//             onValueChange(event.target.value);
//           }}
//           placeholder={placeholder}
//           autoFocus={autoFocus}
//           aria-label={label}
//         />
//         {loading && <span className="journey-mini-loader" aria-label="Searching" />}
//         {(selected || value) && !loading && (
//           <button
//             type="button"
//             className="journey-field-clear"
//             onClick={() => {
//               onSelect(null);
//               onValueChange('');
//             }}
//             aria-label={`Clear ${label.toLowerCase()}`}
//           >
//             <CloseIcon />
//           </button>
//         )}
//       </div>

//       {!selected && hasDropdown && (
//         <div className="journey-search-results" role="listbox">
//           {results.map((item) => (
//             <SearchResult key={item.id} item={item} onSelect={onSelect} />
//           ))}
//           {communityCandidates.length > 0 && (
//             <div className="journey-community-section">
//               <div className="journey-community-title">Help verify nearby suggestions</div>
//               {communityCandidates.map((item) => (
//                 <CommunityCandidate key={item.id} item={item} backend={backend} onVerified={onSelect} />
//               ))}
//             </div>
//           )}
//           {message && <div className="journey-search-message">{message}</div>}
//           {canSuggest && value.trim() && onSuggest && (
//             <button type="button" className="journey-add-location" onClick={() => onSuggest(value.trim())}>
//               <span className="journey-add-location-plus">＋</span>
//               <span><strong>Add “{value.trim()}”</strong><small>Pin it, add evidence and start community verification</small></span>
//               <ArrowIcon size={17} />
//             </button>
//           )}
//         </div>
//       )}
//     </div>
//   );
// }

// function StepTimeline({ option }) {
//   return (
//     <div className="journey-timeline">
//       {(option?.steps || []).map((step, index) => (
//         <div className="journey-step" key={`${step.mode}-${index}-${step.title}`}>
//           <div className={`journey-step-icon mode-${String(step.mode || '').toLowerCase()}`}>
//             <TransitIcon mode={step.mode} size={19} />
//           </div>
//           <div className="journey-step-line" aria-hidden="true" />
//           <div className="journey-step-copy">
//             <strong>{step.title}</strong>
//             <span>{step.detail}</span>
//             {step.live?.status === 'LIVE' && (
//               <small className="journey-live-chip">Live vehicle · {step.live.confidence} confidence</small>
//             )}
//             {step.live?.status === 'SCHEDULE_ESTIMATE' && (
//               <small className="journey-estimate-chip">Service-frequency estimate</small>
//             )}
//           </div>
//           {Number(step.durationMinutes) > 0 && <span className="journey-step-time">{step.durationMinutes} min</span>}
//         </div>
//       ))}
//     </div>
//   );
// }

// export default function JourneyPlanner({
//   backend,
//   userLocation,
//   requestUserLocation,
//   onPlanChange,
//   onLocationUpdate,
//   onFocus,
//   campusMapPicking = false,
//   pickedCampusPoint,
//   onBeginCampusMapPick,
//   onEndCampusMapPick,
//   onClearCampusPickedPoint,
// }) {
//   const [open, setOpen] = useState(false);
//   const [originMode, setOriginMode] = useState('current');
//   const [originQuery, setOriginQuery] = useState('');
//   const [manualOrigin, setManualOrigin] = useState(null);
//   const [destinationQuery, setDestinationQuery] = useState('');
//   const [destination, setDestination] = useState(null);
//   const [preference, setPreference] = useState('fastest');
//   const [planResponse, setPlanResponse] = useState(null);
//   const [activeOption, setActiveOption] = useState(null);
//   const [planning, setPlanning] = useState(false);
//   const [error, setError] = useState('');
//   const [navigating, setNavigating] = useState(false);
//   const [liveOrigin, setLiveOrigin] = useState(null);
//   const [showAlternatives, setShowAlternatives] = useState(false);
//   const [contributionOpen, setContributionOpen] = useState(false);
//   const [contributionName, setContributionName] = useState('');
//   const watchIdRef = useRef(null);
//   const requestControllerRef = useRef(null);
//   const activeRequestKeyRef = useRef('');
//   const lastSuccessfulRequestKeyRef = useRef('');
//   const buildJourneyRef = useRef(null);
//   const onPlanChangeRef = useRef(onPlanChange);
//   const onFocusRef = useRef(onFocus);
//   const effectiveOrigin = originMode === 'manual' ? manualOrigin : (liveOrigin || userLocation);
//   const originLat = Number(effectiveOrigin?.lat);
//   const originLng = Number(effectiveOrigin?.lng);
//   const destinationKey = destination
//     ? String(destination.entityId || destination.id || `${destination.lat}:${destination.lng}`)
//     : '';

//   useEffect(() => {
//     onPlanChangeRef.current = onPlanChange;
//   }, [onPlanChange]);

//   useEffect(() => {
//     onFocusRef.current = onFocus;
//   }, [onFocus]);

//   const publishOption = useCallback((response, option) => {
//     if (!response || !option) {
//       onPlanChangeRef.current?.(null);
//       return;
//     }
//     onPlanChangeRef.current?.({ ...response, recommended: option });
//   }, []);

//   const buildJourney = useCallback(async ({ quiet = false, force = false } = {}) => {
//     if (!backend || !destination || !Number.isFinite(originLat) || !Number.isFinite(originLng)) return;

//     const origin = {
//       lat: originLat,
//       lng: originLng,
//       name: effectiveOrigin?.name || (originMode === 'current' ? 'Your location' : 'Selected start'),
//     };

//     if (!isInsideCampus(origin)) {
//       setError('The selected start is outside the IIT Madras campus navigation area. Choose a campus landmark as the start.');
//       return;
//     }

//     const requestKey = [
//       backend,
//       destinationKey,
//       originLat.toFixed(6),
//       originLng.toFixed(6),
//       preference,
//     ].join('|');

//     // Do not send the same automatic request again after a React re-render.
//     if (!force && (
//       activeRequestKeyRef.current === requestKey ||
//       lastSuccessfulRequestKeyRef.current === requestKey
//     )) {
//       return;
//     }

//     // Cancel an older in-flight request when the user changes start/destination/mode.
//     requestControllerRef.current?.abort();
//     const controller = new AbortController();
//     requestControllerRef.current = controller;
//     activeRequestKeyRef.current = requestKey;

//     if (!quiet) setPlanning(true);
//     setError('');

//     try {
//       const response = await axios.post(
//         `${backend}/api/campus/journey`,
//         {
//           origin,
//           destination: destinationPayload(destination),
//           preference,
//         },
//         { timeout: 15000, signal: controller.signal }
//       );

//       const data = response.data;
//       lastSuccessfulRequestKeyRef.current = requestKey;
//       setPlanResponse(data);
//       setActiveOption(data.recommended);
//       publishOption(data, data.recommended);

//       // JourneyMapLayer owns the route viewport. Do not refocus the map here;
//       // doing so fought fitBounds and caused the visible zoom-in/zoom-out loop.
//     } catch (requestError) {
//       if (axios.isCancel(requestError) || requestError?.code === 'ERR_CANCELED') return;

//       const status = requestError?.response?.status;
//       if (status === 429) {
//         const retryAfter = Number(requestError?.response?.headers?.['retry-after']);
//         setError(
//           Number.isFinite(retryAfter) && retryAfter > 0
//             ? `Journey planning is cooling down. Try again in ${retryAfter} seconds.`
//             : 'Too many journey requests were sent. Please wait about a minute and try again.'
//         );
//       } else {
//         setError(requestError?.response?.data?.message || 'Unable to build this journey right now.');
//       }

//       if (!quiet) {
//         setPlanResponse(null);
//         setActiveOption(null);
//         onPlanChangeRef.current?.(null);
//       }
//     } finally {
//       if (activeRequestKeyRef.current === requestKey) activeRequestKeyRef.current = '';
//       if (requestControllerRef.current === controller) requestControllerRef.current = null;
//       if (!quiet) setPlanning(false);
//     }
//   }, [backend, destination, destinationKey, effectiveOrigin?.name, originLat, originLng, originMode, preference, publishOption]);

//   useEffect(() => {
//     buildJourneyRef.current = buildJourney;
//   }, [buildJourney]);

//   useEffect(() => {
//     if (!destination || !Number.isFinite(originLat) || !Number.isFinite(originLng) || navigating) return undefined;
//     const timer = window.setTimeout(() => buildJourney({ quiet: false, force: false }), 450);
//     return () => window.clearTimeout(timer);
//   }, [buildJourney, destinationKey, navigating, originLat, originLng, preference]);

//   useEffect(() => {
//     if (!navigating) return undefined;
//     const refresh = () => buildJourneyRef.current?.({ quiet: true, force: true });
//     const interval = window.setInterval(refresh, 20000);
//     return () => window.clearInterval(interval);
//   }, [navigating]);

//   useEffect(() => () => {
//     requestControllerRef.current?.abort();
//     if (watchIdRef.current != null && navigator.geolocation) {
//       navigator.geolocation.clearWatch(watchIdRef.current);
//     }
//   }, []);

//   const startLiveNavigation = () => {
//     if (navigating) {
//       setNavigating(false);
//       setLiveOrigin(null);
//       if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
//       watchIdRef.current = null;
//       return;
//     }

//     if (!navigator.geolocation) {
//       setError('Live navigation is not supported by this browser.');
//       return;
//     }
//     setOriginMode('current');
//     setNavigating(true);
//     watchIdRef.current = navigator.geolocation.watchPosition(
//       (position) => {
//         const location = {
//           lat: position.coords.latitude,
//           lng: position.coords.longitude,
//           accuracy: position.coords.accuracy,
//           name: 'Your live location',
//         };
//         setLiveOrigin(location);
//         onLocationUpdate?.(location);
//       },
//       (geoError) => {
//         setNavigating(false);
//         setError(geoError?.code === 1 ? 'Location permission is blocked. Allow location in the browser site settings to start live navigation.' : geoError?.message || 'Unable to follow your live location.');
//       },
//       { enableHighAccuracy: true, maximumAge: 5000, timeout: 12000 }
//     );
//   };

//   const clearJourney = () => {
//     requestControllerRef.current?.abort();
//     activeRequestKeyRef.current = '';
//     lastSuccessfulRequestKeyRef.current = '';
//     setDestination(null);
//     setDestinationQuery('');
//     setPlanResponse(null);
//     setActiveOption(null);
//     setError('');
//     setShowAlternatives(false);
//     onPlanChange?.(null);
//     if (navigating) startLiveNavigation();
//   };

//   const chooseOption = (option) => {
//     setActiveOption(option);
//     setShowAlternatives(false);
//     publishOption(planResponse, option);
//   };

//   const chooseQuickDestination = async (query) => {
//     setOpen(true);
//     setDestinationQuery(query);
//     setError('');
//     try {
//       const response = await axios.get(`${backend}/api/campus/search`, {
//         params: { q: query, limit: 1, ...(effectiveOrigin ? { lat: effectiveOrigin.lat, lng: effectiveOrigin.lng } : {}) },
//         timeout: 8000,
//       });
//       const item = response.data?.results?.find((result) => result.routeable);
//       if (item) {
//         requestControllerRef.current?.abort();
//         activeRequestKeyRef.current = '';
//         lastSuccessfulRequestKeyRef.current = '';
//         setDestination(item);
//         setDestinationQuery('');
//         onFocusRef.current?.(item);
//       }
//     } catch (_) {
//       setError('Search is temporarily unavailable.');
//     }
//   };

//   const alternatives = useMemo(() => {
//     if (!planResponse) return [];
//     return [planResponse.recommended, ...(planResponse.alternatives || [])]
//       .filter(Boolean)
//       .filter((option, index, list) => list.findIndex((candidate) => candidate.id === option.id) === index)
//       .filter((option) => option.id !== activeOption?.id);
//   }, [activeOption?.id, planResponse]);

//   const originReady = Boolean(effectiveOrigin && isInsideCampus(effectiveOrigin));

//   return (
//     <>
//       {!open && (
//         <button type="button" className="journey-launcher" onClick={() => setOpen(true)}>
//           <span className="journey-launcher-orb"><SparkIcon size={18} /></span>
//           <span>
//             <strong>Where are you going?</strong>
//             <small>Search buildings, labs, offices &amp; people</small>
//           </span>
//           <ArrowIcon size={18} />
//         </button>
//       )}

//       {open && !campusMapPicking && <button type="button" className="journey-backdrop" onClick={() => setOpen(false)} aria-label="Close journey planner" />}

//       <aside className={`journey-panel ${open ? 'is-open' : ''} ${campusMapPicking ? 'is-map-picking' : ''}`} aria-hidden={!open}>
//         <div className="journey-panel-handle" aria-hidden="true" />
//         <header className="journey-panel-header">
//           <div>
//             <span className="journey-eyebrow"><SparkIcon size={15} /> IITM CAMPUS NAVIGATOR</span>
//             <h2>Take Me There</h2>
//             <p>Door-to-building guidance with live campus buses and carts.</p>
//           </div>
//           <button type="button" className="journey-close" onClick={() => setOpen(false)} aria-label="Close">
//             <CloseIcon />
//           </button>
//         </header>

//         <div className="journey-panel-scroll">
//           {!activeOption && (
//             <section className="journey-search-card">
//               <div className="journey-origin-toggle" role="tablist" aria-label="Choose start location">
//                 <button
//                   type="button"
//                   className={originMode === 'current' ? 'is-active' : ''}
//                   onClick={() => setOriginMode('current')}
//                 >
//                   <LocationIcon size={17} /> Current location
//                 </button>
//                 <button
//                   type="button"
//                   className={originMode === 'manual' ? 'is-active' : ''}
//                   onClick={() => setOriginMode('manual')}
//                 >
//                   Choose a start
//                 </button>
//               </div>

//               {originMode === 'current' ? (
//                 <div className={`journey-current-location ${originReady ? 'is-ready' : ''}`}>
//                   <span className="journey-location-pulse" />
//                   <div>
//                     <strong>{originReady ? 'Current campus location ready' : 'Current location needed'}</strong>
//                     <span>
//                       {originReady
//                         ? `${Number(effectiveOrigin.lat).toFixed(5)}, ${Number(effectiveOrigin.lng).toFixed(5)}`
//                         : 'Allow location, or choose a campus building as your start.'}
//                     </span>
//                   </div>
//                   {!originReady && (
//                     <button type="button" onClick={requestUserLocation}>Use location</button>
//                   )}
//                 </div>
//               ) : (
//                 <SearchField
//                   backend={backend}
//                   label="Start from"
//                   placeholder="Search a gate, hostel, building or stop"
//                   value={originQuery}
//                   selected={manualOrigin}
//                   onValueChange={setOriginQuery}
//                   onSelect={(item) => {
//                     setManualOrigin(item);
//                     if (item) setOriginQuery('');
//                   }}
//                   origin={userLocation}
//                 />
//               )}

//               <SearchField
//                 backend={backend}
//                 label="Destination"
//                 placeholder="Try NAC2, a lab, office or professor"
//                 value={destinationQuery}
//                 selected={destination}
//                 onValueChange={setDestinationQuery}
//                 onSelect={(item) => {
//                   requestControllerRef.current?.abort();
//                   activeRequestKeyRef.current = '';
//                   lastSuccessfulRequestKeyRef.current = '';
//                   setDestination(item);
//                   if (item) {
//                     setDestinationQuery('');
//                     onFocusRef.current?.(item);
//                   }
//                 }}
//                 origin={effectiveOrigin}
//                 autoFocus={originReady}
//                 onSuggest={(name) => {
//                   setContributionName(name);
//                   onClearCampusPickedPoint?.();
//                   setContributionOpen(true);
//                 }}
//               />

//               {!destination && (
//                 <div className="journey-quick-row">
//                   <span>Popular</span>
//                   {QUICK_DESTINATIONS.map((item) => (
//                     <button type="button" key={item.query} onClick={() => chooseQuickDestination(item.query)}>
//                       {item.label}
//                     </button>
//                   ))}
//                 </div>
//               )}

//               <div className="journey-preference-row">
//                 <span>Optimise for</span>
//                 {[
//                   ['fastest', 'Fastest'],
//                   ['transit', 'Bus / cart'],
//                   ['walking', 'Walking'],
//                 ].map(([value, label]) => (
//                   <button
//                     type="button"
//                     key={value}
//                     className={preference === value ? 'is-active' : ''}
//                     onClick={() => setPreference(value)}
//                   >
//                     {label}
//                   </button>
//                 ))}
//               </div>

//               {planning && (
//                 <div className="journey-planning-state">
//                   <span className="journey-route-loader" />
//                   <div>
//                     <strong>Finding your best campus route</strong>
//                     <span>Comparing walking, live vehicles and campus stops…</span>
//                   </div>
//                 </div>
//               )}
//               {error && <div className="journey-error">{error}</div>}
//             </section>
//           )}

//           {activeOption && planResponse && (
//             <>
//               <section className="journey-result-hero">
//                 <div className="journey-result-topline">
//                   <span className="journey-result-mode">
//                     <TransitIcon mode={activeOption.mode} size={18} />
//                     {activeOption.type === 'walking' ? 'Walk' : activeOption.mode === 'CART' ? 'Cart + walk' : 'Bus + walk'}
//                   </span>
//                   <span className={`journey-confidence confidence-${activeOption.confidence || 'medium'}`}>
//                     {activeOption.confidence || 'medium'} confidence
//                   </span>
//                 </div>
//                 <div className="journey-arrival-grid">
//                   <div>
//                     <small>ARRIVE</small>
//                     <strong>{formatArrival(activeOption.arrivalTime)}</strong>
//                   </div>
//                   <div>
//                     <small>TOTAL</small>
//                     <strong>{activeOption.durationMinutes} min</strong>
//                   </div>
//                   <div>
//                     <small>DISTANCE</small>
//                     <strong>{formatDistance(activeOption.distanceM)}</strong>
//                   </div>
//                 </div>
//                 <div className="journey-destination-line">
//                   <span className="journey-destination-dot" />
//                   <div>
//                     <small>DESTINATION</small>
//                     <strong>{planResponse.destination?.name}</strong>
//                     {destination?.building?.roomNo && (
//                       <span>{destination.building.shortName || destination.building.name} · Room {destination.building.roomNo}</span>
//                     )}
//                   </div>
//                 </div>
//               </section>

//               {activeOption.liveVehicle && (
//                 <section className="journey-live-card">
//                   <span className="journey-live-dot" />
//                   <div>
//                     <small>LIVE {activeOption.mode === 'CART' ? 'CART' : 'BUS'}</small>
//                     <strong>{activeOption.liveVehicle.vehicleNo}</strong>
//                     <span>Reaches your stop in about {activeOption.liveVehicle.etaMinutes} min</span>
//                   </div>
//                   <span className="journey-live-badge">LIVE</span>
//                 </section>
//               )}

//               <section className="journey-directions-card">
//                 <div className="journey-section-title">
//                   <div>
//                     <span>YOUR ROUTE</span>
//                     <h3>{activeOption.title}</h3>
//                   </div>
//                   <button type="button" onClick={clearJourney}>Change</button>
//                 </div>
//                 <StepTimeline option={activeOption} />
//               </section>

//               {alternatives.length > 0 && (
//                 <section className="journey-alternatives-card">
//                   <button type="button" className="journey-alternatives-toggle" onClick={() => setShowAlternatives((value) => !value)}>
//                     <span>
//                       <strong>{alternatives.length} other route{alternatives.length === 1 ? '' : 's'}</strong>
//                       <small>Compare walking and transit options</small>
//                     </span>
//                     <ChevronIcon open={showAlternatives} />
//                   </button>
//                   {showAlternatives && (
//                     <div className="journey-alternative-list">
//                       {alternatives.map((option) => (
//                         <button type="button" key={option.id} onClick={() => chooseOption(option)}>
//                           <span className={`journey-alt-icon mode-${String(option.mode).toLowerCase()}`}>
//                             <TransitIcon mode={option.mode} size={18} />
//                           </span>
//                           <span>
//                             <strong>{option.title}</strong>
//                             <small>{option.subtitle}</small>
//                           </span>
//                           <b>{option.durationMinutes} min</b>
//                         </button>
//                       ))}
//                     </div>
//                   )}
//                 </section>
//               )}

//               {error && <div className="journey-error">{error}</div>}

//               <div className="journey-action-bar">
//                 <button type="button" className={navigating ? 'is-navigating' : ''} onClick={startLiveNavigation}>
//                   <span>{navigating ? 'Live navigation on' : 'Start live navigation'}</span>
//                   <small>{navigating ? 'Route refreshes as you move' : 'Follow your position on the map'}</small>
//                   <ArrowIcon size={19} />
//                 </button>
//               </div>
//             </>
//           )}
//         </div>
//       </aside>

//       <CampusContributionPanel
//         open={contributionOpen}
//         mapPicking={campusMapPicking}
//         backend={backend}
//         initialName={contributionName}
//         userLocation={userLocation}
//         pickedPoint={pickedCampusPoint}
//         onBeginMapPick={onBeginCampusMapPick}
//         onEndMapPick={onEndCampusMapPick}
//         onSubmitted={() => {
//           setDestinationQuery(contributionName);
//         }}
//         onClose={() => {
//           setContributionOpen(false);
//           onEndCampusMapPick?.();
//           onClearCampusPickedPoint?.();
//         }}
//       />
//     </>
//   );
// }

// // import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// // import axios from 'axios';
// // import './JourneyPlanner.css';

// // const CAMPUS_BOUNDS = {
// //   south: 12.978,
// //   west: 80.217,
// //   north: 13.0115,
// //   east: 80.2485,
// // };

// // const QUICK_DESTINATIONS = [
// //   { label: 'NAC2', query: 'NAC2' },
// //   { label: 'CLT', query: 'CLT' },
// //   { label: 'Main Gate', query: 'Main Gate' },
// //   { label: 'Research Park', query: 'Research Park' },
// // ];

// // function SearchIcon({ size = 20 }) {
// //   return (
// //     <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
// //       <path d="m21 21-4.35-4.35m2.35-5.15a7.5 7.5 0 1 1-15 0 7.5 7.5 0 0 1 15 0Z" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
// //     </svg>
// //   );
// // }

// // function ArrowIcon({ size = 18 }) {
// //   return (
// //     <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
// //       <path d="M5 12h14m-5-5 5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
// //     </svg>
// //   );
// // }

// // function LocationIcon({ size = 20 }) {
// //   return (
// //     <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
// //       <path d="M12 21s6-5.1 6-11a6 6 0 1 0-12 0c0 5.9 6 11 6 11Z" stroke="currentColor" strokeWidth="1.8" />
// //       <circle cx="12" cy="10" r="2.2" stroke="currentColor" strokeWidth="1.8" />
// //     </svg>
// //   );
// // }

// // function SparkIcon({ size = 18 }) {
// //   return (
// //     <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
// //       <path d="M12 3c.65 3.25 2.75 5.35 6 6-3.25.65-5.35 2.75-6 6-.65-3.25-2.75-5.35-6-6 3.25-.65 5.35-2.75 6-6Z" fill="currentColor" />
// //       <path d="M19 15c.28 1.38 1.12 2.22 2.5 2.5-1.38.28-2.22 1.12-2.5 2.5-.28-1.38-1.12-2.22-2.5-2.5 1.38-.28 2.22-1.12 2.5-2.5Z" fill="currentColor" opacity=".72" />
// //     </svg>
// //   );
// // }

// // function CloseIcon() {
// //   return (
// //     <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
// //       <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
// //     </svg>
// //   );
// // }

// // function ChevronIcon({ open }) {
// //   return (
// //     <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ transform: open ? 'rotate(180deg)' : 'none' }}>
// //       <path d="m7 9 5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
// //     </svg>
// //   );
// // }

// // function TransitIcon({ mode, size = 20 }) {
// //   if (mode === 'WALK') {
// //     return (
// //       <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
// //         <circle cx="13" cy="4.5" r="2" fill="currentColor" />
// //         <path d="m10.5 8 3 2.5 2.5 4m-5.5-6.5-2 4 3 2.5-2 5m4-9.5-1 5 4 4.5m-5-7.5H7.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
// //       </svg>
// //     );
// //   }
// //   if (mode === 'WAIT') {
// //     return (
// //       <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
// //         <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.8" />
// //         <path d="M12 7.5V12l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
// //       </svg>
// //     );
// //   }
// //   if (mode === 'ALIGHT') {
// //     return (
// //       <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
// //         <path d="M12 3v12m0 0-4-4m4 4 4-4M5 20h14" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
// //       </svg>
// //     );
// //   }
// //   return (
// //     <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
// //       <rect x="5" y="3.5" width="14" height="15" rx="3" stroke="currentColor" strokeWidth="1.8" />
// //       <path d="M7.5 13.5h9M8 7h8M8 20v-1.5m8 1.5v-1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
// //       <circle cx="8.5" cy="16" r="1" fill="currentColor" />
// //       <circle cx="15.5" cy="16" r="1" fill="currentColor" />
// //     </svg>
// //   );
// // }

// // function isInsideCampus(location) {
// //   return Boolean(
// //     location &&
// //       Number(location.lat) >= CAMPUS_BOUNDS.south &&
// //       Number(location.lat) <= CAMPUS_BOUNDS.north &&
// //       Number(location.lng) >= CAMPUS_BOUNDS.west &&
// //       Number(location.lng) <= CAMPUS_BOUNDS.east
// //   );
// // }

// // function formatArrival(value) {
// //   if (!value) return '—';
// //   const date = new Date(value);
// //   if (Number.isNaN(date.getTime())) return '—';
// //   return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
// // }

// // function formatDistance(metres) {
// //   const value = Number(metres);
// //   if (!Number.isFinite(value)) return '';
// //   if (value >= 1000) return `${(value / 1000).toFixed(value >= 10000 ? 0 : 1)} km`;
// //   return `${Math.round(value)} m`;
// // }

// // function destinationPayload(item) {
// //   if (!item) return null;
// //   return {
// //     id: item.id,
// //     entityId: item.entityId,
// //     name: item.name,
// //     fullName: item.fullName,
// //     type: item.type,
// //     lat: item.lat,
// //     lng: item.lng,
// //     source: item.source,
// //     building: item.building,
// //   };
// // }

// // function SearchResult({ item, onSelect }) {
// //   const meta = [item.subtitle, item.distanceM != null ? `${formatDistance(item.distanceM)} away` : '']
// //     .filter(Boolean)
// //     .join(' · ');
// //   const buildingLine = item.building
// //     ? [item.building.shortName || item.building.name, item.building.roomNo ? `Room ${item.building.roomNo}` : '']
// //         .filter(Boolean)
// //         .join(' · ')
// //     : '';

// //   return (
// //     <button
// //       type="button"
// //       className={`journey-search-result ${item.routeable ? '' : 'is-unmapped'}`}
// //       onClick={() => item.routeable && onSelect(item)}
// //       disabled={!item.routeable}
// //     >
// //       <span className="journey-search-result-icon"><LocationIcon size={18} /></span>
// //       <span className="journey-search-result-copy">
// //         <strong>{item.name}</strong>
// //         <span>{buildingLine || meta}</span>
// //         {buildingLine && <small>{meta}</small>}
// //         {!item.routeable && <small>Building location has not been mapped yet</small>}
// //       </span>
// //       {item.routeable && <span className="journey-search-result-arrow"><ArrowIcon size={16} /></span>}
// //     </button>
// //   );
// // }

// // function SearchField({
// //   backend,
// //   label,
// //   placeholder,
// //   value,
// //   selected,
// //   onValueChange,
// //   onSelect,
// //   origin,
// //   autoFocus,
// // }) {
// //   const [results, setResults] = useState([]);
// //   const [loading, setLoading] = useState(false);
// //   const [message, setMessage] = useState('');
// //   const requestRef = useRef(0);

// //   useEffect(() => {
// //     if (selected || !value.trim() || !backend) {
// //       setResults([]);
// //       setMessage('');
// //       return undefined;
// //     }

// //     const requestId = ++requestRef.current;
// //     const timer = window.setTimeout(async () => {
// //       setLoading(true);
// //       setMessage('');
// //       try {
// //         const response = await axios.get(`${backend}/api/campus/search`, {
// //           params: {
// //             q: value.trim(),
// //             limit: 12,
// //             ...(origin ? { lat: origin.lat, lng: origin.lng } : {}),
// //           },
// //           timeout: 8000,
// //         });
// //         if (requestRef.current !== requestId) return;
// //         const nextResults = response.data?.results || [];
// //         setResults(nextResults);
// //         if (!nextResults.length) setMessage('No matching campus location found. Try a building code, lab, office or person name.');
// //       } catch (error) {
// //         if (requestRef.current !== requestId) return;
// //         setResults([]);
// //         setMessage(error?.response?.data?.message || 'Search is temporarily unavailable.');
// //       } finally {
// //         if (requestRef.current === requestId) setLoading(false);
// //       }
// //     }, 260);

// //     return () => window.clearTimeout(timer);
// //   }, [backend, origin?.lat, origin?.lng, selected, value]);

// //   return (
// //     <div className="journey-field-group">
// //       <label>{label}</label>
// //       <div className={`journey-search-box ${selected ? 'has-selection' : ''}`}>
// //         <SearchIcon size={19} />
// //         <input
// //           value={selected ? selected.name : value}
// //           onChange={(event) => {
// //             if (selected) onSelect(null);
// //             onValueChange(event.target.value);
// //           }}
// //           placeholder={placeholder}
// //           autoFocus={autoFocus}
// //           aria-label={label}
// //         />
// //         {loading && <span className="journey-mini-loader" aria-label="Searching" />}
// //         {(selected || value) && !loading && (
// //           <button
// //             type="button"
// //             className="journey-field-clear"
// //             onClick={() => {
// //               onSelect(null);
// //               onValueChange('');
// //             }}
// //             aria-label={`Clear ${label.toLowerCase()}`}
// //           >
// //             <CloseIcon />
// //           </button>
// //         )}
// //       </div>

// //       {!selected && (results.length > 0 || message) && (
// //         <div className="journey-search-results" role="listbox">
// //           {results.map((item) => (
// //             <SearchResult key={item.id} item={item} onSelect={onSelect} />
// //           ))}
// //           {message && <div className="journey-search-message">{message}</div>}
// //         </div>
// //       )}
// //     </div>
// //   );
// // }

// // function StepTimeline({ option }) {
// //   return (
// //     <div className="journey-timeline">
// //       {(option?.steps || []).map((step, index) => (
// //         <div className="journey-step" key={`${step.mode}-${index}-${step.title}`}>
// //           <div className={`journey-step-icon mode-${String(step.mode || '').toLowerCase()}`}>
// //             <TransitIcon mode={step.mode} size={19} />
// //           </div>
// //           <div className="journey-step-line" aria-hidden="true" />
// //           <div className="journey-step-copy">
// //             <strong>{step.title}</strong>
// //             <span>{step.detail}</span>
// //             {step.live?.status === 'LIVE' && (
// //               <small className="journey-live-chip">Live vehicle · {step.live.confidence} confidence</small>
// //             )}
// //             {step.live?.status === 'SCHEDULE_ESTIMATE' && (
// //               <small className="journey-estimate-chip">Service-frequency estimate</small>
// //             )}
// //           </div>
// //           {Number(step.durationMinutes) > 0 && <span className="journey-step-time">{step.durationMinutes} min</span>}
// //         </div>
// //       ))}
// //     </div>
// //   );
// // }

// // export default function JourneyPlanner({
// //   backend,
// //   userLocation,
// //   requestUserLocation,
// //   onPlanChange,
// //   onLocationUpdate,
// //   onFocus,
// // }) {
// //   const [open, setOpen] = useState(false);
// //   const [originMode, setOriginMode] = useState('current');
// //   const [originQuery, setOriginQuery] = useState('');
// //   const [manualOrigin, setManualOrigin] = useState(null);
// //   const [destinationQuery, setDestinationQuery] = useState('');
// //   const [destination, setDestination] = useState(null);
// //   const [preference, setPreference] = useState('fastest');
// //   const [planResponse, setPlanResponse] = useState(null);
// //   const [activeOption, setActiveOption] = useState(null);
// //   const [planning, setPlanning] = useState(false);
// //   const [error, setError] = useState('');
// //   const [navigating, setNavigating] = useState(false);
// //   const [liveOrigin, setLiveOrigin] = useState(null);
// //   const [showAlternatives, setShowAlternatives] = useState(false);
// //   const watchIdRef = useRef(null);
// //   const effectiveOrigin = originMode === 'manual' ? manualOrigin : (liveOrigin || userLocation);

// //   const publishOption = useCallback(
// //     (response, option) => {
// //       if (!response || !option) {
// //         onPlanChange?.(null);
// //         return;
// //       }
// //       onPlanChange?.({ ...response, recommended: option });
// //     },
// //     [onPlanChange]
// //   );

// //   const buildJourney = useCallback(async ({ quiet = false } = {}) => {
// //     if (!backend || !effectiveOrigin || !destination) return;
// //     if (!isInsideCampus(effectiveOrigin)) {
// //       setError('The selected start is outside the IIT Madras campus navigation area. Choose a campus landmark as the start.');
// //       return;
// //     }
// //     if (!quiet) setPlanning(true);
// //     setError('');
// //     try {
// //       const response = await axios.post(
// //         `${backend}/api/campus/journey`,
// //         {
// //           origin: {
// //             lat: effectiveOrigin.lat,
// //             lng: effectiveOrigin.lng,
// //             name: effectiveOrigin.name || (originMode === 'current' ? 'Your location' : 'Selected start'),
// //           },
// //           destination: destinationPayload(destination),
// //           preference,
// //         },
// //         { timeout: 15000 }
// //       );
// //       const data = response.data;
// //       setPlanResponse(data);
// //       setActiveOption(data.recommended);
// //       publishOption(data, data.recommended);
// //       onFocus?.(data.destination);
// //     } catch (requestError) {
// //       setError(requestError?.response?.data?.message || 'Unable to build this journey right now.');
// //       if (!quiet) {
// //         setPlanResponse(null);
// //         setActiveOption(null);
// //         onPlanChange?.(null);
// //       }
// //     } finally {
// //       if (!quiet) setPlanning(false);
// //     }
// //   }, [backend, destination, effectiveOrigin, onFocus, onPlanChange, originMode, preference, publishOption]);

// //   useEffect(() => {
// //     if (!destination || !effectiveOrigin) return undefined;
// //     const timer = window.setTimeout(() => buildJourney(), 320);
// //     return () => window.clearTimeout(timer);
// //   }, [buildJourney, destination, effectiveOrigin?.lat, effectiveOrigin?.lng, preference]);

// //   useEffect(() => {
// //     if (!navigating) return undefined;
// //     const interval = window.setInterval(() => buildJourney({ quiet: true }), 20000);
// //     return () => window.clearInterval(interval);
// //   }, [buildJourney, navigating]);

// //   useEffect(() => () => {
// //     if (watchIdRef.current != null && navigator.geolocation) {
// //       navigator.geolocation.clearWatch(watchIdRef.current);
// //     }
// //   }, []);

// //   const startLiveNavigation = () => {
// //     if (navigating) {
// //       setNavigating(false);
// //       setLiveOrigin(null);
// //       if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
// //       watchIdRef.current = null;
// //       return;
// //     }

// //     if (!navigator.geolocation) {
// //       setError('Live navigation is not supported by this browser.');
// //       return;
// //     }
// //     setOriginMode('current');
// //     setNavigating(true);
// //     watchIdRef.current = navigator.geolocation.watchPosition(
// //       (position) => {
// //         const location = {
// //           lat: position.coords.latitude,
// //           lng: position.coords.longitude,
// //           accuracy: position.coords.accuracy,
// //           name: 'Your live location',
// //         };
// //         setLiveOrigin(location);
// //         onLocationUpdate?.(location);
// //       },
// //       (geoError) => {
// //         setNavigating(false);
// //         setError(geoError?.code === 1 ? 'Location permission is blocked. Allow location in the browser site settings to start live navigation.' : geoError?.message || 'Unable to follow your live location.');
// //       },
// //       { enableHighAccuracy: true, maximumAge: 5000, timeout: 12000 }
// //     );
// //   };

// //   const clearJourney = () => {
// //     setDestination(null);
// //     setDestinationQuery('');
// //     setPlanResponse(null);
// //     setActiveOption(null);
// //     setError('');
// //     setShowAlternatives(false);
// //     onPlanChange?.(null);
// //     if (navigating) startLiveNavigation();
// //   };

// //   const chooseOption = (option) => {
// //     setActiveOption(option);
// //     setShowAlternatives(false);
// //     publishOption(planResponse, option);
// //   };

// //   const chooseQuickDestination = async (query) => {
// //     setOpen(true);
// //     setDestinationQuery(query);
// //     setError('');
// //     try {
// //       const response = await axios.get(`${backend}/api/campus/search`, {
// //         params: { q: query, limit: 1, ...(effectiveOrigin ? { lat: effectiveOrigin.lat, lng: effectiveOrigin.lng } : {}) },
// //         timeout: 8000,
// //       });
// //       const item = response.data?.results?.find((result) => result.routeable);
// //       if (item) {
// //         setDestination(item);
// //         setDestinationQuery('');
// //       }
// //     } catch (_) {
// //       setError('Search is temporarily unavailable.');
// //     }
// //   };

// //   const alternatives = useMemo(() => {
// //     if (!planResponse) return [];
// //     return [planResponse.recommended, ...(planResponse.alternatives || [])]
// //       .filter(Boolean)
// //       .filter((option, index, list) => list.findIndex((candidate) => candidate.id === option.id) === index)
// //       .filter((option) => option.id !== activeOption?.id);
// //   }, [activeOption?.id, planResponse]);

// //   const originReady = Boolean(effectiveOrigin && isInsideCampus(effectiveOrigin));

// //   return (
// //     <>
// //       {!open && (
// //         <button type="button" className="journey-launcher" onClick={() => setOpen(true)}>
// //           <span className="journey-launcher-orb"><SparkIcon size={18} /></span>
// //           <span>
// //             <strong>Where are you going?</strong>
// //             <small>Search buildings, labs, offices &amp; people</small>
// //           </span>
// //           <ArrowIcon size={18} />
// //         </button>
// //       )}

// //       {open && <button type="button" className="journey-backdrop" onClick={() => setOpen(false)} aria-label="Close journey planner" />}

// //       <aside className={`journey-panel ${open ? 'is-open' : ''}`} aria-hidden={!open}>
// //         <div className="journey-panel-handle" aria-hidden="true" />
// //         <header className="journey-panel-header">
// //           <div>
// //             <span className="journey-eyebrow"><SparkIcon size={15} /> IITM CAMPUS NAVIGATOR</span>
// //             <h2>Take Me There</h2>
// //             <p>Door-to-building guidance with live campus buses and carts.</p>
// //           </div>
// //           <button type="button" className="journey-close" onClick={() => setOpen(false)} aria-label="Close">
// //             <CloseIcon />
// //           </button>
// //         </header>

// //         <div className="journey-panel-scroll">
// //           {!activeOption && (
// //             <section className="journey-search-card">
// //               <div className="journey-origin-toggle" role="tablist" aria-label="Choose start location">
// //                 <button
// //                   type="button"
// //                   className={originMode === 'current' ? 'is-active' : ''}
// //                   onClick={() => setOriginMode('current')}
// //                 >
// //                   <LocationIcon size={17} /> Current location
// //                 </button>
// //                 <button
// //                   type="button"
// //                   className={originMode === 'manual' ? 'is-active' : ''}
// //                   onClick={() => setOriginMode('manual')}
// //                 >
// //                   Choose a start
// //                 </button>
// //               </div>

// //               {originMode === 'current' ? (
// //                 <div className={`journey-current-location ${originReady ? 'is-ready' : ''}`}>
// //                   <span className="journey-location-pulse" />
// //                   <div>
// //                     <strong>{originReady ? 'Current campus location ready' : 'Current location needed'}</strong>
// //                     <span>
// //                       {originReady
// //                         ? `${Number(effectiveOrigin.lat).toFixed(5)}, ${Number(effectiveOrigin.lng).toFixed(5)}`
// //                         : 'Allow location, or choose a campus building as your start.'}
// //                     </span>
// //                   </div>
// //                   {!originReady && (
// //                     <button type="button" onClick={requestUserLocation}>Use location</button>
// //                   )}
// //                 </div>
// //               ) : (
// //                 <SearchField
// //                   backend={backend}
// //                   label="Start from"
// //                   placeholder="Search a gate, hostel, building or stop"
// //                   value={originQuery}
// //                   selected={manualOrigin}
// //                   onValueChange={setOriginQuery}
// //                   onSelect={(item) => {
// //                     setManualOrigin(item);
// //                     if (item) setOriginQuery('');
// //                   }}
// //                   origin={userLocation}
// //                 />
// //               )}

// //               <SearchField
// //                 backend={backend}
// //                 label="Destination"
// //                 placeholder="Try NAC2, a lab, office or professor"
// //                 value={destinationQuery}
// //                 selected={destination}
// //                 onValueChange={setDestinationQuery}
// //                 onSelect={(item) => {
// //                   setDestination(item);
// //                   if (item) setDestinationQuery('');
// //                 }}
// //                 origin={effectiveOrigin}
// //                 autoFocus={originReady}
// //               />

// //               {!destination && (
// //                 <div className="journey-quick-row">
// //                   <span>Popular</span>
// //                   {QUICK_DESTINATIONS.map((item) => (
// //                     <button type="button" key={item.query} onClick={() => chooseQuickDestination(item.query)}>
// //                       {item.label}
// //                     </button>
// //                   ))}
// //                 </div>
// //               )}

// //               <div className="journey-preference-row">
// //                 <span>Optimise for</span>
// //                 {[
// //                   ['fastest', 'Fastest'],
// //                   ['transit', 'Bus / cart'],
// //                   ['walking', 'Walking'],
// //                 ].map(([value, label]) => (
// //                   <button
// //                     type="button"
// //                     key={value}
// //                     className={preference === value ? 'is-active' : ''}
// //                     onClick={() => setPreference(value)}
// //                   >
// //                     {label}
// //                   </button>
// //                 ))}
// //               </div>

// //               {planning && (
// //                 <div className="journey-planning-state">
// //                   <span className="journey-route-loader" />
// //                   <div>
// //                     <strong>Finding your best campus route</strong>
// //                     <span>Comparing walking, live vehicles and campus stops…</span>
// //                   </div>
// //                 </div>
// //               )}
// //               {error && <div className="journey-error">{error}</div>}
// //             </section>
// //           )}

// //           {activeOption && planResponse && (
// //             <>
// //               <section className="journey-result-hero">
// //                 <div className="journey-result-topline">
// //                   <span className="journey-result-mode">
// //                     <TransitIcon mode={activeOption.mode} size={18} />
// //                     {activeOption.type === 'walking' ? 'Walk' : activeOption.mode === 'CART' ? 'Cart + walk' : 'Bus + walk'}
// //                   </span>
// //                   <span className={`journey-confidence confidence-${activeOption.confidence || 'medium'}`}>
// //                     {activeOption.confidence || 'medium'} confidence
// //                   </span>
// //                 </div>
// //                 <div className="journey-arrival-grid">
// //                   <div>
// //                     <small>ARRIVE</small>
// //                     <strong>{formatArrival(activeOption.arrivalTime)}</strong>
// //                   </div>
// //                   <div>
// //                     <small>TOTAL</small>
// //                     <strong>{activeOption.durationMinutes} min</strong>
// //                   </div>
// //                   <div>
// //                     <small>DISTANCE</small>
// //                     <strong>{formatDistance(activeOption.distanceM)}</strong>
// //                   </div>
// //                 </div>
// //                 <div className="journey-destination-line">
// //                   <span className="journey-destination-dot" />
// //                   <div>
// //                     <small>DESTINATION</small>
// //                     <strong>{planResponse.destination?.name}</strong>
// //                     {destination?.building?.roomNo && (
// //                       <span>{destination.building.shortName || destination.building.name} · Room {destination.building.roomNo}</span>
// //                     )}
// //                   </div>
// //                 </div>
// //               </section>

// //               {activeOption.liveVehicle && (
// //                 <section className="journey-live-card">
// //                   <span className="journey-live-dot" />
// //                   <div>
// //                     <small>LIVE {activeOption.mode === 'CART' ? 'CART' : 'BUS'}</small>
// //                     <strong>{activeOption.liveVehicle.vehicleNo}</strong>
// //                     <span>Reaches your stop in about {activeOption.liveVehicle.etaMinutes} min</span>
// //                   </div>
// //                   <span className="journey-live-badge">LIVE</span>
// //                 </section>
// //               )}

// //               <section className="journey-directions-card">
// //                 <div className="journey-section-title">
// //                   <div>
// //                     <span>YOUR ROUTE</span>
// //                     <h3>{activeOption.title}</h3>
// //                   </div>
// //                   <button type="button" onClick={clearJourney}>Change</button>
// //                 </div>
// //                 <StepTimeline option={activeOption} />
// //               </section>

// //               {alternatives.length > 0 && (
// //                 <section className="journey-alternatives-card">
// //                   <button type="button" className="journey-alternatives-toggle" onClick={() => setShowAlternatives((value) => !value)}>
// //                     <span>
// //                       <strong>{alternatives.length} other route{alternatives.length === 1 ? '' : 's'}</strong>
// //                       <small>Compare walking and transit options</small>
// //                     </span>
// //                     <ChevronIcon open={showAlternatives} />
// //                   </button>
// //                   {showAlternatives && (
// //                     <div className="journey-alternative-list">
// //                       {alternatives.map((option) => (
// //                         <button type="button" key={option.id} onClick={() => chooseOption(option)}>
// //                           <span className={`journey-alt-icon mode-${String(option.mode).toLowerCase()}`}>
// //                             <TransitIcon mode={option.mode} size={18} />
// //                           </span>
// //                           <span>
// //                             <strong>{option.title}</strong>
// //                             <small>{option.subtitle}</small>
// //                           </span>
// //                           <b>{option.durationMinutes} min</b>
// //                         </button>
// //                       ))}
// //                     </div>
// //                   )}
// //                 </section>
// //               )}

// //               {error && <div className="journey-error">{error}</div>}

// //               <div className="journey-action-bar">
// //                 <button type="button" className={navigating ? 'is-navigating' : ''} onClick={startLiveNavigation}>
// //                   <span>{navigating ? 'Live navigation on' : 'Start live navigation'}</span>
// //                   <small>{navigating ? 'Route refreshes as you move' : 'Follow your position on the map'}</small>
// //                   <ArrowIcon size={19} />
// //                 </button>
// //               </div>
// //             </>
// //           )}
// //         </div>
// //       </aside>
// //     </>
// //   );
// // }
